import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, ArrowRight, Loader2, CheckCircle2, Shield, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ticketing-hub-18/artifacts/9kyejo6n_Screenshot%202026-04-08%20at%205.02.54%E2%80%AFPM.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to reset password. The link may have expired.');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-10 text-center">
          <div className="flex justify-center mb-5">
            <img src={LOGO_URL} alt="Intelvolks" className="h-10 object-contain" />
          </div>
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-800 mb-2">Invalid Reset Link</h2>
          <p className="text-sm text-slate-500 mb-6">This password reset link is invalid or missing. Please request a new one from the login page.</p>
          <button onClick={() => navigate('/login')}
            className="w-full h-[48px] text-sm font-semibold rounded-lg bg-[#002FA7] text-white hover:bg-[#002585] transition-all"
            data-testid="back-to-login-btn">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-10">
        <div className="flex justify-center mb-6">
          <img src={LOGO_URL} alt="Intelvolks" className="h-10 object-contain" />
        </div>

        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">Set New Password</h1>
          <p className="mt-2 text-sm text-slate-500">Choose a strong password that meets all requirements.</p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-600 flex items-center gap-2" data-testid="reset-error">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-sm text-emerald-700 flex items-center gap-2" data-testid="reset-success">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> {success}
          </div>
        )}

        {!success && (
          <form onSubmit={handleResetPassword} className="space-y-5">
            <div>
              <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 block mb-2">New Password</label>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="password" placeholder="Min 8 chars, upper, lower, number, special" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-11 h-[48px] text-sm rounded-lg outline-none border border-slate-200 text-[#0A0A0A] bg-white focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/10 transition-all"
                  data-testid="new-password-input" />
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">Must include uppercase, lowercase, number, and special character.</p>
            </div>
            <div>
              <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 block mb-2">Confirm Password</label>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="password" placeholder="Repeat password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-11 h-[48px] text-sm rounded-lg outline-none border border-slate-200 text-[#0A0A0A] bg-white focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/10 transition-all"
                  data-testid="confirm-password-input" />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full h-[48px] text-sm font-semibold rounded-lg flex items-center justify-center gap-2 bg-[#002FA7] text-white hover:bg-[#002585] disabled:opacity-50 transition-all hover:-translate-y-[1px]"
              data-testid="reset-submit-btn">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Reset Password <ArrowRight className="h-4 w-4" /></>}
            </button>
            <button type="button" onClick={() => navigate('/login')}
              className="w-full text-center text-sm text-slate-500 hover:text-slate-700" data-testid="reset-back-to-login">
              Back to login
            </button>
          </form>
        )}

        <div className="mt-8 flex items-center justify-center gap-1">
          <Shield size={10} className="text-slate-300" />
          <span className="text-[10px] text-slate-300 font-mono">SOC II Compliant</span>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
